import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PrescriptionModal } from "./components/PrescriptionModal";
import { PrescriptionApprovalHeader } from "./PrescriptionApprovalHeader";
import { PrescriptionApprovalFilters } from "./PrescriptionApprovalFilters";
import { PrescriptionApprovalTable } from "./PrescriptionApprovalTable";
import { SubscriptionApprovalTable } from "./SubscriptionApprovalTable";
import { PrescriptionApprovalBulkActions } from "./PrescriptionApprovalBulkActions";
import { PrescriptionApprovalModals } from "./PrescriptionApprovalModals";
import { ManageSubscriptionModal } from "./components/ManageSubscriptionModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Clock, FileText } from "lucide-react";
import {
  subscriptionsData,
  Subscription,
  getEffectiveStatus,
} from "@/lib/subscriptionsData";
import { usePrescriptionApprovalData } from "./PrescriptionApprovalData";
import PrescriptionApprovalSkeleton from "./PrescriptionApprovalSkeleton";
import { setIsPatientDetailsLoading } from "@/dispatchers/patientDispatchers";
import useTitanApp from "@/hooks/useTitanApp";
import { useLocation } from "react-router-dom";
import { usePrescriptionData } from "@/lib/prescriptionData";
import {
  setPrescriptionImagePreview,
  setPreviewPrescription,
} from "@/dispatchers/patientPrescriptionDispatcher";
import { DeclinePrescriptionModal } from "./DeclinePrescriptionModal";

interface PrescriptionApprovalProps {
  isDarkMode: boolean;
  prescriptions: any[];
  setPrescriptions: React.Dispatch<React.SetStateAction<any[]>>;
}

export function PrescriptionApproval() {
  const {
    darkMode,
    setPrescriptions,
    isPatientPrescriptionsLoading,
    prescriptions,
    isPharmacyServiceLoading,
    services,
    handleNavigateToConsultation,
    showPrintPreviewLoading,
  } = usePrescriptionApprovalData();

  const {
    handlePatientPrescriptionBulkUpdate,
    handleVerifyPinForPrescriptionApproval,
    handleShowPrescription,
    patientPrescriptions,
    prescriptionImagePreview,
    previewPrescription,
  } = usePrescriptionData();

  const isDarkMode = darkMode;
  // const prescriptions = initialPatientPrescriptions;
  // const setPrescriptions = setInitialPatientPrescriptions;

  // Secondary tab state
  const [activeSecondaryTab, setActiveSecondaryTab] = useState<
    "outstanding" | "subscriptions" | "history"
  >("outstanding");

  // Filter and sort state
  const [searchFilter, setSearchFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [sortField, setSortField] = useState<
    "patientName" | "consultationDate" | "prescribedBy" | "serviceType"
  >("consultationDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Subscription state management
  const [subscriptions, setSubscriptions] = useState<Subscription[]>(
    subscriptionsData.slice(0, 6),
  );
  const [subscriptionSortField, setSubscriptionSortField] = useState<
    "patientName" | "medication" | "prescribedBy" | "nextSupplyDate" | "status"
  >("nextSupplyDate");
  const [subscriptionSortDirection, setSubscriptionSortDirection] = useState<
    "asc" | "desc"
  >("asc");

  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [decliningPrescription, setDecliningPrescription] = useState<
    any | null
  >(null);

  // Filter and sort prescriptions based on active tab
  const filteredAndSortedPrescriptions = useMemo(() => {
    let filtered = prescriptions.filter((p) => {
      if (activeSecondaryTab === "outstanding") {
        return p.status === "pending";
      } else {
        return p.status === "approved" || p.status === "rejected";
      }
    });

    // Apply search filter
    if (searchFilter) {
      filtered = filtered.filter(
        (prescription) =>
          prescription.patientName
            .toLowerCase()
            .includes(searchFilter.toLowerCase()) ||
          prescription.prescribedBy
            .toLowerCase()
            .includes(searchFilter.toLowerCase()) ||
          prescription.medications.some((med: any) =>
            med.medication.toLowerCase().includes(searchFilter.toLowerCase()),
          ),
      );
    }

    // Apply service filter
    if (serviceFilter !== "all") {
      filtered = filtered.filter(
        (prescription) => prescription.serviceType === serviceFilter,
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case "patientName":
          aValue = a.patientName.toLowerCase();
          bValue = b.patientName.toLowerCase();
          break;
        case "consultationDate":
          aValue = new Date(a.consultationDate).getTime();
          bValue = new Date(b.consultationDate).getTime();
          break;
        case "prescribedBy":
          aValue = a.prescribedBy.toLowerCase();
          bValue = b.prescribedBy.toLowerCase();
          break;
        case "serviceType":
          aValue = a.serviceType.toLowerCase();
          bValue = b.serviceType.toLowerCase();
          break;
        default:
          return 0;
      }

      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return filtered;
  }, [
    prescriptions,
    searchFilter,
    serviceFilter,
    sortField,
    sortDirection,
    activeSecondaryTab,
  ]);

  // Filter and sort subscriptions
  const filteredAndSortedSubscriptions = useMemo(() => {
    let filtered = subscriptions.slice();

    // Apply search filter
    if (searchFilter) {
      filtered = filtered.filter(
        (subscription) =>
          subscription.patientName
            .toLowerCase()
            .includes(searchFilter.toLowerCase()) ||
          subscription.prescribedBy
            .toLowerCase()
            .includes(searchFilter.toLowerCase()) ||
          subscription.medication
            .toLowerCase()
            .includes(searchFilter.toLowerCase()),
      );
    }

    // Apply service filter
    if (serviceFilter !== "all") {
      filtered = filtered.filter(
        (subscription) => subscription.serviceType === serviceFilter,
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (subscriptionSortField) {
        case "patientName":
          aValue = a.patientName.toLowerCase();
          bValue = b.patientName.toLowerCase();
          break;
        case "medication":
          aValue = a.medication.toLowerCase();
          bValue = b.medication.toLowerCase();
          break;
        case "prescribedBy":
          aValue = a.prescribedBy.toLowerCase();
          bValue = b.prescribedBy.toLowerCase();
          break;
        case "nextSupplyDate":
          aValue = new Date(a.nextSupplyDate).getTime();
          bValue = new Date(b.nextSupplyDate).getTime();
          break;
        case "status":
          aValue = getEffectiveStatus(a);
          bValue = getEffectiveStatus(b);
          break;
        default:
          return 0;
      }

      if (subscriptionSortDirection === "asc") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return filtered;
  }, [
    subscriptions,
    searchFilter,
    serviceFilter,
    subscriptionSortField,
    subscriptionSortDirection,
  ]);

  const currentPrescriptions = filteredAndSortedPrescriptions;
  const urgentCount = currentPrescriptions.filter(
    (p) => p.priority === "urgent" && p.status === "pending",
  ).length;

  // Handle sorting
  const handleSort = (
    field: "patientName" | "consultationDate" | "prescribedBy" | "serviceType",
  ) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Handle subscription sorting
  const handleSubscriptionSort = (
    field:
      | "patientName"
      | "medication"
      | "prescribedBy"
      | "nextSupplyDate"
      | "status",
  ) => {
    if (subscriptionSortField === field) {
      setSubscriptionSortDirection(
        subscriptionSortDirection === "asc" ? "desc" : "asc",
      );
    } else {
      setSubscriptionSortField(field);
      setSubscriptionSortDirection("asc");
    }
  };

  const [approvalModalOpen, setApprovalModalOpen] = useState<string | null>(
    null,
  );
  const [rejectionModalOpen, setRejectionModalOpen] = useState<string | null>(
    null,
  );
  const [editModalOpen, setEditModalOpen] = useState<string | null>(null);
  const [editingPrescription, setEditingPrescription] = useState<any | null>(
    null,
  );

  // Consultation summary modal state
  const [consultationSummaryOpen, setConsultationSummaryOpen] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState<any | null>(
    null,
  );

  // Manage subscription modal state
  const [manageSubscriptionOpen, setManageSubscriptionOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] =
    useState<Subscription | null>(null);

  // Multi-select state
  const [selectedPrescriptions, setSelectedPrescriptions] = useState<
    Set<string>
  >(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Subscription multi-select state
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<
    Set<string>
  >(new Set());
  const [selectAllSubscriptions, setSelectAllSubscriptions] = useState(false);

  // PIN authorisation state
  const [showPinAuthModal, setShowPinAuthModal] = useState(false);
  const [authPin, setAuthPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [pinError, setPinError] = useState("");
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authStep, setAuthStep] = useState(1); // 1: PIN, 2: Delivery Options
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<
    "pharmacy" | "print" | ""
  >("");
  const [selectedPharmacy, setSelectedPharmacy] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const navigateFromDashboard =
    location.state && location?.state?.moveFromDashboard;
  const selectedPatientName =
    location.state && location?.state?.selectedPatientName;
  const selectedPatientAddress =
    location.state && location?.state?.selectedPatientAddress;
  const pharmacyAvgVal = location.state && location?.state?.pharmacyAvgVal;

  // Pharmacy data (in real app, this would come from API)
  const pharmacies = [
    {
      id: "1",
      name: "Boots Pharmacy",
      address: "123 High Street, London SW1A 1AA",
    },
    {
      id: "2",
      name: "Lloyds Pharmacy",
      address: "456 Oxford Street, London W1C 1DX",
    },
    {
      id: "3",
      name: "Independent Pharmacy",
      address: "789 King's Road, London SW3 4NX",
    },
    {
      id: "4",
      name: "Superdrug Pharmacy",
      address: "321 Baker Street, London NW1 6XE",
    },
  ];

  // Mock certificate data (in real app, this would come from user profile/API)
  const activeCertificate = {};

  const handleApprovePrescription = (prescriptionId: string) => {
    setPrescriptions((prev) =>
      prev.map((p) =>
        p.id === prescriptionId ? { ...p, status: "approved" } : p,
      ),
    );
    setApprovalModalOpen(null);
  };

  const handleRejectPrescription = (prescriptionId: string) => {
    setPrescriptions((prev) =>
      prev.map((p) =>
        p.id === prescriptionId ? { ...p, status: "rejected" } : p,
      ),
    );
    setRejectionModalOpen(null);
  };

  const handleProcessPrescription = (prescription: any) => {
    setEditingPrescription(prescription);
    setEditModalOpen(prescription.id);
  };

  const handleSaveEditedPrescription = (updatedPrescription: any) => {
    if (!editingPrescription) return;

    const medicationItem =
      updatedPrescription.items?.[0] || updatedPrescription;

    setPrescriptions((prev) =>
      prev.map((p) =>
        p.id === editingPrescription.id
          ? {
              ...p,
              medication: medicationItem.medication || p.medication,
              dosage: medicationItem.dosage || p.dosage,
              quantity: medicationItem.quantity || p.quantity,
            }
          : p,
      ),
    );

    setEditModalOpen(null);
    setEditingPrescription(null);
  };

  const handleViewConsultationSummary = (prescription: any) => {
    const consultation = {
      id: prescription.id,
      patientName: prescription.patientName,
      date: prescription.consultationDate,
      time: prescription.consultationTime,
      prescribedBy: prescription.prescribedBy,
      serviceType: prescription.serviceType,
      summary: prescription.summary,
      // summary: `Consultation summary for ${
      //   prescription.patientName
      // } on ${new Date(prescription.consultationDate).toLocaleDateString(
      //   "en-GB"
      // )} at ${
      //   prescription.consultationTime
      // }. Service: ${prescription.serviceType
      //   .replace("-", " ")
      //   .replace(/\b\w/g, (l) => l.toUpperCase())}. Prescribed by ${
      //   prescription.prescribedBy
      // }.`,
    };

    setSelectedConsultation(consultation);
    setConsultationSummaryOpen(true);
  };

  const handleGoToConsultation = async (data: any) => {
    const id = data?.id;
    const result = patientPrescriptions.find(
      (x) => x.patientPrescription?._id === id,
    );
    setIsPatientDetailsLoading(true);
    const patient = result?.patient;
    if (!patient?.patientID) {
      return;
    }
    const patientConsultation = result?.patientConsultation;
    handleNavigateToConsultation(patient?.patientID, patientConsultation);
  };

  // Multi-select handlers
  const handleSelectPrescription = (
    prescriptionId: string,
    checked: boolean,
  ) => {
    setSelectedPrescriptions((prev) => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(prescriptionId);
      } else {
        newSelected.delete(prescriptionId);
      }
      return newSelected;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      const allIds = new Set(currentPrescriptions.map((p) => p.id));
      setSelectedPrescriptions(allIds);
    } else {
      setSelectedPrescriptions(new Set());
    }
  };

  const handleBulkApprove = () => {
    setShowPinAuthModal(true);
  };

  const handlePinAuthorisation = async () => {
    setPinError("");

    // Validate PIN
    if (!authPin) {
      setPinError("PIN is required for authorisation");
      return;
    }

    if (authPin.length !== 4) {
      setPinError("PIN must be exactly 4 digits");
      return;
    }

    if (!/^\d+$/.test(authPin)) {
      setPinError("PIN must contain only numbers");
      return;
    }

    // Simulate PIN verification
    setIsAuthenticating(true);

    const result = await handleVerifyPinForPrescriptionApproval(authPin);

    if (result === null || result === "False") {
      setIsAuthenticating(false);
      setPinError("PIN verification failed. Please try again");
      return;
    }

    setTimeout(() => {
      // Move to delivery options step
      setAuthStep(2);
      setIsAuthenticating(false);
    }, 1500);
  };

  const handleClosePinModal = () => {
    setShowPinAuthModal(false);
    setAuthPin("");
    setPinError("");
    setIsAuthenticating(false);
    setAuthStep(1);
    setSelectedDeliveryOption("");
    setSelectedPharmacy("");
  };

  const handleProcessPrescriptions = async () => {
    if (selectedDeliveryOption === "pharmacy" && selectedPharmacy) {
      const pharmacy = pharmacies.find((p) => p.id === selectedPharmacy);
    } else if (selectedDeliveryOption === "print") {
      // In real app, this would trigger bulk print functionality
    }

    // Approve all selected prescriptions
    setPrescriptions((prev) =>
      prev.map((p) =>
        selectedPrescriptions.has(p.id) ? { ...p, status: "approved" } : p,
      ),
    );
    const payload = {
      prescriptionIds: selectedPrescriptions,
      selectedPharmacy: selectedPharmacy,
    };
    await handlePatientPrescriptionBulkUpdate(payload);

    // Reset all states
    setSelectedPrescriptions(new Set());
    setSelectAll(false);
    setShowPinAuthModal(false);
    setAuthPin("");
    setPinError("");
    setIsAuthenticating(false);
    setAuthStep(1);
    setSelectedDeliveryOption("");
    setSelectedPharmacy("");
  };

  const handleBulkReject = () => {
    setPrescriptions((prev) =>
      prev.map((p) =>
        selectedPrescriptions.has(p.id) ? { ...p, status: "rejected" } : p,
      ),
    );
    setSelectedPrescriptions(new Set());
    setSelectAll(false);
  };

  const handleClearSelection = () => {
    setSelectedPrescriptions(new Set());
    setSelectAll(false);
  };

  // Subscription handlers
  const handleSelectSubscription = (
    subscriptionId: string,
    checked: boolean,
  ) => {
    setSelectedSubscriptions((prev) => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(subscriptionId);
      } else {
        newSelected.delete(subscriptionId);
      }
      return newSelected;
    });
  };

  const handleSelectAllSubscriptions = (checked: boolean) => {
    setSelectAllSubscriptions(checked);
    if (checked) {
      const allIds = new Set(filteredAndSortedSubscriptions.map((s) => s.id));
      setSelectedSubscriptions(allIds);
    } else {
      setSelectedSubscriptions(new Set());
    }
  };

  const handleProcessSubscription = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setManageSubscriptionOpen(true);
  };

  // Clear selection when switching tabs
  const handleTabChange = (
    tab: "outstanding" | "subscriptions" | "history",
  ) => {
    setActiveSecondaryTab(tab);
    setSelectedPrescriptions(new Set());
    setSelectAll(false);
    setSelectedSubscriptions(new Set());
    setSelectAllSubscriptions(false);
  };

  const handleDeclinePrescription = (prescription: any) => {
    setDecliningPrescription(prescription);
    setDeclineModalOpen(true);
  };

  const handleConfirmDecline = (
    prescriptionId: string,
    reason: string,
    comments: string,
  ) => {
    setPrescriptions((prev) =>
      prev.map((p) =>
        p.id === prescriptionId
          ? {
              ...p,
              status: "declined",
              declineReason: reason,
              declineComments: comments,
              declinedAt: new Date().toISOString(),
              declinedBy: "Current User", // In production, use actual user
            }
          : p,
      ),
    );
    setDeclineModalOpen(false);
    setDecliningPrescription(null);
  };

  // Secondary tabs configuration
  const secondaryTabs = [
    {
      id: "outstanding" as const,
      label: "Outstanding",
      icon: Clock,
    },
    // {
    //   id: "subscriptions" as const,
    //   label: "Subscriptions",
    //   icon: RotateCcw,
    // },
    // {
    //   id: "history" as const,
    //   label: "History",
    //   icon: CheckCircle,
    // },
  ];

  return (
    <>
      {isPatientPrescriptionsLoading ? (
        <PrescriptionApprovalSkeleton />
      ) : (
        <div className="space-y-6">
          <PrescriptionApprovalHeader
            isDarkMode={isDarkMode}
            pendingCount={
              prescriptions.filter((p) => p.status === "pending").length
            }
            urgentCount={urgentCount}
          />

          {/* Secondary Tabs */}
          <div
            className={`border-b px-6 py-3 ${
              isDarkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="flex space-x-6">
              {secondaryTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 py-2 text-xs font-medium transition-all duration-200 relative ${
                    activeSecondaryTab === tab.id
                      ? isDarkMode
                        ? "text-white"
                        : "text-gray-900"
                      : isDarkMode
                        ? "text-gray-400 hover:text-gray-200"
                        : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                  {activeSecondaryTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="px-6 space-y-6">
            <PrescriptionApprovalFilters
              isDarkMode={isDarkMode}
              searchFilter={searchFilter}
              setSearchFilter={setSearchFilter}
              serviceFilter={serviceFilter}
              setServiceFilter={setServiceFilter}
              services={services}
            />

            {activeSecondaryTab === "subscriptions" ? (
              <SubscriptionApprovalTable
                isDarkMode={isDarkMode}
                subscriptions={filteredAndSortedSubscriptions}
                sortField={subscriptionSortField}
                sortDirection={subscriptionSortDirection}
                onSort={handleSubscriptionSort}
                selectAll={selectAllSubscriptions}
                onSelectAll={handleSelectAllSubscriptions}
                selectedSubscriptions={selectedSubscriptions}
                onSelectSubscription={handleSelectSubscription}
                onProcessSubscription={handleProcessSubscription}
              />
            ) : (
              <PrescriptionApprovalTable
                isDarkMode={isDarkMode}
                prescriptions={currentPrescriptions}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                selectAll={selectAll}
                onSelectAll={handleSelectAll}
                selectedPrescriptions={selectedPrescriptions}
                onSelectPrescription={handleSelectPrescription}
                onViewPrescription={handleShowPrescription}
                onProcessPrescription={handleProcessPrescription}
                onViewConsultationSummary={handleViewConsultationSummary}
                onGoToConsultation={handleGoToConsultation}
                onDeclinePrescription={handleDeclinePrescription}
              />
            )}

            {/* Show bulk actions for outstanding prescriptions and subscriptions */}
            {(activeSecondaryTab === "outstanding" ||
              activeSecondaryTab === "subscriptions") && (
              <PrescriptionApprovalBulkActions
                isDarkMode={isDarkMode}
                selectedCount={
                  activeSecondaryTab === "subscriptions"
                    ? selectedSubscriptions.size
                    : selectedPrescriptions.size
                }
                onBulkApprove={
                  activeSecondaryTab === "subscriptions"
                    ? () => {
                        setSelectedSubscriptions(new Set());
                        setSelectAllSubscriptions(false);
                      }
                    : handleBulkApprove
                }
                onClearSelection={
                  activeSecondaryTab === "subscriptions"
                    ? () => {
                        setSelectedSubscriptions(new Set());
                        setSelectAllSubscriptions(false);
                      }
                    : handleClearSelection
                }
              />
            )}
          </div>

          <PrescriptionApprovalModals
            showPinAuthModal={showPinAuthModal}
            onClosePinModal={handleClosePinModal}
            authStep={authStep}
            activeCertificate={activeCertificate}
            selectedPrescriptionsCount={selectedPrescriptions.size}
            authPin={authPin}
            setAuthPin={setAuthPin}
            showPin={showPin}
            setShowPin={setShowPin}
            pinError={pinError}
            setPinError={setPinError}
            isAuthenticating={isAuthenticating}
            onPinAuthorisation={handlePinAuthorisation}
            selectedDeliveryOption={selectedDeliveryOption}
            setSelectedDeliveryOption={setSelectedDeliveryOption}
            selectedPharmacy={selectedPharmacy}
            setSelectedPharmacy={setSelectedPharmacy}
            pharmacies={pharmacies}
            onProcessPrescriptions={handleProcessPrescriptions}
            setAuthStep={setAuthStep}
            prescriptionImagePreview={prescriptionImagePreview}
            setPrescriptionImagePreview={setPrescriptionImagePreview}
            previewPrescription={previewPrescription}
            setPreviewPrescription={setPreviewPrescription}
            showPrintPreviewLoading={showPrintPreviewLoading}
          />

          {/* Edit Prescription Modal */}
          {editingPrescription && (
            <PrescriptionModal
              isOpen={!!editModalOpen}
              onOpenChange={(open) => {
                if (!open) {
                  setEditModalOpen(null);
                  setEditingPrescription(null);
                }
              }}
              isDarkMode={isDarkMode}
              editMode={true}
              existingPrescription={{
                id: editingPrescription.id,
                patientName: editingPrescription.patientName,
                patientInitials: editingPrescription.patientInitials,
                patientNHS: editingPrescription.patientNHS,
                medication:
                  editingPrescription.medications.length > 1
                    ? `${editingPrescription.medications[0]?.medication} (+${
                        editingPrescription.medications.length - 1
                      } more)`
                    : editingPrescription.medications[0]?.medication || "",
                dosage: editingPrescription.medications[0]?.dosage || "",
                quantity: editingPrescription.medications[0]?.quantity || "",
                duration: editingPrescription.medications[0]?.duration || "",

                indication:
                  editingPrescription.medications.length > 1
                    ? `${editingPrescription.medications[0]?.indication} (+ other medications)`
                    : editingPrescription.medications[0]?.indication || "",
                notes: editingPrescription.notes,
                priority: editingPrescription.priority,
                allergies: editingPrescription.allergies,
                prescribedBy: editingPrescription.prescribedBy,
                medications: editingPrescription.medications,
                pharmacyID: editingPrescription.pharmacyID,
                base64Image: editingPrescription.prescriptionImageUrl || "",
                prescriptionCategory:
                  editingPrescription.prescriptionCategory || 0,
              }}
              onSave={handleSaveEditedPrescription}
              onViewPrescription={handleShowPrescription}
            />
          )}

          {/* Consultation Summary Modal */}
          <Dialog
            open={consultationSummaryOpen}
            onOpenChange={setConsultationSummaryOpen}
          >
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Consultation Summary
                </DialogTitle>
              </DialogHeader>
              {selectedConsultation && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-gray-50">
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Patient
                      </p>
                      <p className="text-sm text-gray-900">
                        {selectedConsultation.patientName}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Date & Time
                      </p>
                      <p className="text-sm text-gray-900">
                        {new Date(selectedConsultation.date).toLocaleDateString(
                          "en-GB",
                        )}{" "}
                        at {selectedConsultation.time}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Prescribed By
                      </p>
                      <p className="text-sm text-gray-900">
                        {selectedConsultation.prescribedBy}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Service Type
                      </p>
                      <p className="text-sm text-gray-900">
                        {selectedConsultation.serviceType
                          .replace("-", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="mb-2 text-sm font-medium text-gray-600">
                      Summary
                    </p>
                    <div className="p-4 bg-white border rounded-lg">
                      <p className="text-sm text-gray-700">
                        {selectedConsultation.summary.replace(
                          /\s*(snomedCodes|SnomedCodes):\s*(\[.*?\]|null)\s*$/s,
                          "",
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Manage Subscription Modal */}
          <ManageSubscriptionModal
            isOpen={manageSubscriptionOpen}
            onOpenChange={setManageSubscriptionOpen}
            subscription={selectedSubscription}
            isDarkMode={isDarkMode}
          />

          {/* Decline Prescription Modal */}
          {decliningPrescription && (
            <DeclinePrescriptionModal
              open={declineModalOpen}
              onOpenChange={setDeclineModalOpen}
              prescription={decliningPrescription}
              onDecline={handleConfirmDecline}
              isDarkMode={isDarkMode}
            />
          )}
        </div>
      )}
    </>
  );
}
